import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { CanvaImportStatusDto } from "shared";
import { io, type Socket } from "socket.io-client";
import { Api } from "../api.config";
import { QueryKeys } from "../LinkGen";

export function useCanvaImportStream(
	companyId: number,
	importId: number | null,
	onStatus?: (status: CanvaImportStatusDto) => void,
): void {
	const queryClient = useQueryClient();
	const onStatusRef = useRef(onStatus);
	onStatusRef.current = onStatus;

	useEffect(() => {
		if (importId === null) {
			return;
		}

		const queryKey = QueryKeys.Integrations.Canva.Import(companyId, importId);
		let closed = false;
		let pollTimer: ReturnType<typeof setInterval> | null = null;
		let socket: Socket | null = null;

		const applyStatus = (status: CanvaImportStatusDto) => {
			queryClient.setQueryData(queryKey, status);
			onStatusRef.current?.(status);
		};

		const stopPolling = () => {
			if (pollTimer) {
				clearInterval(pollTimer);
				pollTimer = null;
			}
		};

		const pollOnce = async () => {
			try {
				const res = await Api.CanvaImportStatus(importId);
				if (closed) {
					return;
				}
				applyStatus(res.data);
				if (res.data.status === "completed" || res.data.status === "failed") {
					stopPolling();
				}
			} catch {
				// Keep polling until the import finishes or the modal closes.
			}
		};

		const startPollingFallback = () => {
			if (pollTimer) {
				return;
			}
			void pollOnce();
			pollTimer = setInterval(() => {
				void pollOnce();
			}, 2000);
		};

		// Always poll as a safety net. Auth disconnect after a successful
		// socket handshake does not fire connect_error, which previously left
		// the UI stuck on "Pending".
		startPollingFallback();

		socket = io("/canva-import", {
			withCredentials: true,
			transports: ["websocket", "polling"],
		});

		socket.on("connect", () => {
			socket?.emit("subscribe", { importId });
		});

		socket.on("status", (status: CanvaImportStatusDto) => {
			applyStatus(status);
			if (status.status === "completed" || status.status === "failed") {
				stopPolling();
			}
		});

		socket.on("disconnect", () => {
			if (!closed) {
				startPollingFallback();
			}
		});

		socket.on("connect_error", () => {
			if (!closed) {
				startPollingFallback();
			}
		});

		socket.on("error", () => {
			if (!closed) {
				startPollingFallback();
			}
		});

		return () => {
			closed = true;
			stopPolling();
			socket?.disconnect();
			socket = null;
		};
	}, [companyId, importId, queryClient]);
}
